import { MockTicket, useAppTheme, useSettings, useTutorialsCompleted } from '@capsicum/common';
import {
	BaseTutorialCard,
	Card,
	CardBuilder,
	type ChipListItem,
	Label,
	SsuHud,
	useTutorial,
	useTutorialStyles,
	useWindowSize
} from '@capsicum/common-web';
import { AccessIcon, BundleTicketIcon, ReverseAuctionIcon } from '@capsicum/common-web/icons';
import { TokenParticipationAmount } from '@capsicum/socios-core-web';
import { useEffect, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';

export const useRewardsCategoryTutorial = (isLoading: boolean, chipCategories: Array<ChipListItem>) => {
	const { isOpen, setup } = useTutorial();
	const { settings } = useSettings();
	const {
		dimensions: { width }
	} = useWindowSize();

	const { highlightedAreaStyle, popoverStyles } = useTutorialStyles();
	const { breakpoints, colors } = useTheme();
	const { theme } = useAppTheme();
	const { isAlreadyCompleted } = useTutorialsCompleted();

	const scrollIntoView = () =>
		new Promise(resolve => {
			window.scrollTo({ behavior: 'instant', top: 120 });

			resolve(true);
		});

	const resetView = () =>
		new Promise(resolve => {
			window.scrollTo({ behavior: 'smooth', top: 0 });

			resolve(true);
		});

	const fakeTutorialCard = useMemo(() => {
		if (isAlreadyCompleted('rewards-category')) return;

		return isOpen
			? new CardBuilder()
					.setMediaContent(
						<div>
							<S.Ribbon $backgroundColor={colors.decorativeBronze}>VIP Premium</S.Ribbon>
							<S.Wrapper>
								<S.StyledCardImage alt="tutorial image" src={MockTicket} />
								<S.ChipWrapper>
									<div data-tutorial="rewardsCategoryLabelTutorial">
										<Label
											icon={() => <AccessIcon height={16} width={16} />}
											label="25 BAR"
											size="default"
											variant="default"
										/>
									</div>
								</S.ChipWrapper>
							</S.Wrapper>
						</div>,
						{ height: 186 }
					)
					.setHeader(
						<>
							<div data-tutorial="rewardsCategoryHammerTutorial" style={{ padding: 1 }}>
								<ReverseAuctionIcon color={colors.icons.main.enabled} height={24} width={24} />
							</div>
							<div data-tutorial="rewardsPackageProductTutorial" style={{ padding: 1 }}>
								<BundleTicketIcon color={colors.icons.main.enabled} height={24} width={24} />
							</div>
						</>,
						'end'
					)
					.setHeader(
						<SsuHud size="small" testID="product-card-ssu-hud">
							{33000}
						</SsuHud>,
						'start'
					)
					.setTitle('FC Barcelona - Atlético de Madrid')
					.setCardStyle({
						maxWidth: 176,
						width: '50%'
					})
					.setProps({
						'data-tutorial': 'rewardsCategoryCardTutorial',
						testID: 'fanshop-category-tutorial-card'
					})
					.build()
			: undefined;
	}, [isOpen]);

	useEffect(() => {
		if (
			!isLoading &&
			!!chipCategories.length &&
			settings.areTutorialsEnabled.rewardsCategory &&
			!isAlreadyCompleted('rewards-category')
		) {
			setup('rewards-category', {
				steps: [
					{
						content: (
							<BaseTutorialCard
								content="rewards.fanshop.tutorials.category.step1.contentRewardPoints"
								title="rewards.fanshop.tutorials.category.step1.title"
							/>
						),
						padding: 0,
						position: 'bottom',
						selector: '[data-tutorial="rewardsCategoryCardTutorial"]',
						stepInteraction: false,
						styles: {
							highlightedArea: highlightedAreaStyle({ disableInteraction: true }),
							popover: popoverStyles({ marginLeft: 0 })
						}
					},
					{
						content: <BaseTutorialCard content="rewards.fanshop.tutorials.category.step2.content" />,
						padding: 0,
						position: 'bottom',
						selector: '[data-tutorial="rewardsCategoryLabelTutorial"]',
						stepInteraction: false,
						styles: {
							highlightedArea: base => ({
								...base,
								display: 'block',
								height: base?.height + 10,
								pointerEvents: 'none',
								rx: 20,
								stroke: theme.colors.borders.success.enabled,
								strokeDasharray: 5,
								strokeWidth: 2,
								width: base?.width + 10,
								x: base?.x - 5,
								y: base?.y - 5
							}),
							maskArea: base => ({
								...base,
								rx: 20
							}),
							popover: popoverStyles()
						}
					},
					{
						content: (
							<BaseTutorialCard
								content="rewards.fanshop.tutorials.category.step3.contentRewardPoints"
								title="rewards.fanshop.tutorials.category.step3.title"
							/>
						),
						padding: 0,
						position: 'bottom',
						selector: '[data-tutorial="rewardsCategoryHammerTutorial"]',
						stepInteraction: false,
						styles: {
							highlightedArea: base => ({
								...base,
								display: 'block',
								height: base?.height + 10,
								pointerEvents: 'none',
								rx: 20,
								stroke: theme.colors.borders.success.enabled,
								strokeDasharray: 5,
								strokeWidth: 2,
								width: base?.width + 10,
								x: base?.x - 5,
								y: base?.y - 5
							}),
							maskArea: base => ({
								...base,
								rx: 15
							}),
							popover: popoverStyles()
						}
					},
					{
						content: (
							<BaseTutorialCard
								content="rewards.fanshop.tutorials.category.step4.content"
								title="rewards.fanshop.tutorials.category.step4.title"
							/>
						),
						padding: 0,
						position: 'bottom',
						selector: '[data-tutorial="rewardsPackageProductTutorial"]',
						stepInteraction: false,
						styles: {
							highlightedArea: base => ({
								...base,
								display: 'block',
								height: base?.height + 10,
								pointerEvents: 'none',
								rx: 20,
								stroke: theme.colors.borders.success.enabled,
								strokeDasharray: 5,
								strokeWidth: 2,
								width: base?.width + 10,
								x: base?.x - 5,
								y: base?.y - 5
							}),
							maskArea: base => ({
								...base,
								rx: 15
							}),
							popover: popoverStyles()
						}
					}
				],
				threshold: { x: 0, y: 0 }
			});
		}
	}, [highlightedAreaStyle, isLoading, setup, settings.areTutorialsEnabled.rewardsCategory]);

	useEffect(() => {
		if (!isAlreadyCompleted('rewards-category') && width < breakpoints.points.mobile.pageWidth) scrollIntoView();
	}, [isAlreadyCompleted, width]);

	useEffect(() => {
		if (!isOpen) {
			resetView();
		}
	}, [isOpen]);

	return {
		fakeTutorialCard
	};
};

namespace S {
	export const Wrapper = styled.div`
		position: relative;
	`;

	export const StyledCardImage = styled(Card.Image)`
		height: 160px;
	`;

	export const StyledTokenParticipationAmount = styled(TokenParticipationAmount)`
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
		position: absolute;
		left: ${({ theme }) => theme.spacings.small};
		top: ${({ theme }) => theme.spacings.large};
		z-index: 2;
	`;

	export const ChipWrapper = styled.div`
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
		position: absolute;
		left: ${({ theme }) => theme.spacings.small};
		top: ${({ theme }) => theme.spacings.large};
		z-index: 2;
	`;

	export const Ribbon = styled.div<{ $backgroundColor: string | undefined }>`
		background-color: ${({ $backgroundColor, theme }) =>
			$backgroundColor || theme.colors.backgrounds.brand.enabled};
		display: flex;
		align-items: center;
		justify-content: center;
		text-transform: capitalize;
		${({ theme }) => theme.fonts.text.text4}
		color: ${({ theme }) => theme.colors.text.onDark.enabled};
		width: 100%;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		padding: ${({ theme }) => theme.spacings.xxxsmall};
	`;
}
